/**
 * HW UX — site-wide interactions (VERSION 1.6 - Design Refresh)
 *
 *   1. Scroll state: compact header, progress line, back-to-top
 *   2. Scroll reveal for cards / headings / feature blocks
 *   3. Cursor spotlight on product cards
 *   4. Count-up for hero stats
 *   5. "Added" confirmation on the Add to Cart button
 *
 * Presentation only — never touches cart, price or variant logic.
 * Loaded with `defer` from snippets/hw-ux.liquid.
 */
(function () {
  'use strict';

  if (window.__hwUx) return;
  window.__hwUx = true;

  var doc = document;
  var root = doc.documentElement;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  function ready(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* ── 1. Scroll state ─────────────────────────────────────────────────────── */
  function initScroll() {
    var bar = doc.createElement('div');
    bar.className = 'hw-scrollbar';
    bar.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(bar);

    var top = doc.createElement('button');
    top.type = 'button';
    top.className = 'hw-totop';
    top.setAttribute('aria-label', 'Back to top');
    top.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>';
    top.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    doc.body.appendChild(top);

    // Publish the live header height so sticky sub-navs (Parts, 3D, Apparel,
    // Tuning) and the sticky product gallery sit right under the header while
    // it grows / shrinks, instead of at a height measured once on load.
    var header = doc.getElementById('SiteHeader') || doc.querySelector('.site-header') || doc.querySelector('.header-section');
    var headerSection = doc.querySelector('.header-section');
    if (header && 'ResizeObserver' in window) {
      var setHeaderH = function () {
        // the section wrapper collapses to 0 when the header overlays a hero
        var h = Math.max(header.offsetHeight, headerSection ? headerSection.offsetHeight : 0);
        root.style.setProperty('--hw-header-h', h + 'px');
      };
      new ResizeObserver(setHeaderH).observe(header);
      setHeaderH();
      root.classList.add('hw-hdr');
    }
    var resizeTimer = 0;
    function headerChanged() {
      // let any script that measures the header on "resize" re-measure
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        try { window.dispatchEvent(new Event('resize')); } catch (e) {}
      }, 360);
    }

    var ticking = false;
    var scrolled = false;

    function update() {
      ticking = false;
      var y = window.pageYOffset || root.scrollTop || 0;
      var max = (root.scrollHeight - window.innerHeight) || 1;

      // hysteresis so the header never flickers at the threshold
      if (!scrolled && y > 140) { scrolled = true; root.classList.add('hw-scrolled'); headerChanged(); }
      else if (scrolled && y < 50) { scrolled = false; root.classList.remove('hw-scrolled'); headerChanged(); }

      bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, y / max)).toFixed(4) + ')';
      top.classList.toggle('is-on', y > window.innerHeight * 1.2);
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ── 2. Scroll reveal ────────────────────────────────────────────────────── */
  var REVEAL = [
    // [selector, mode]
    ['.hw-parts-card, .hw-3d-card, .hwa-card, .hw-drops-card, .hw-tune-card, .hw-ti-card', 'up'],
    ['.hw-cat-card, .hw-social-card, .hw-tune-ov-card, .hw-tune-policy-card, .hw-tune-step', 'up'],
    ['.hw-map-feature, .hw-map-spec-card, .hw-map-install__loc-card, .hw-hd-case', 'up'],
    ['.hw-story__card, .hw-story__panel, .hw-story__media, .hw-story__note, .hw-story__prose', 'up'],
    ['.search-content .grid-item, .hwa-assure, .hw-brands', 'up'],
    ['.hw-section-hd, .hw-tune-section__header, .hw-tune-process__header, .hw-tune-policy__header, .hw-tune-overview__header', 'up'],
    ['.hw-map-section-title, .hw-hd-split__title, .hw-hd-intro__title, .hw-ti-section-title, .hw-story__head, .hw-story__block-head', 'up'],
    ['.hw-map-spotlight__content, .hw-tune-cal-feature__content, .hw-youtube__text, .hw-map-why__left, .hw-hd-split__body', 'left'],
    ['.hw-map-spotlight__img-col, .hw-tune-cal-feature__img-col, .hw-youtube__embed, .hw-map-why__right, .hw-hd-split__media', 'right'],
    ['.hw-newsletter__inner, .hw-map-free-cal__inner, .hw-map-install__warning, .hw-3d-suggest__inner', 'zoom']
  ];

  function initReveal() {
    if (reduce || inEditor || !('IntersectionObserver' in window)) return;

    root.classList.add('hw-anim');
    var vh = window.innerHeight || 800;
    var queue = [];
    var flushing = false;

    function finish(el) {
      // hand the element back to its own styles (hover transforms etc.)
      el.removeAttribute('data-hw-reveal');
      el.classList.remove('is-in');
      el.style.removeProperty('--hw-d');
    }

    function flush() {
      flushing = false;
      // stagger whatever arrived together, left-to-right / top-to-bottom
      queue.sort(function (a, b) {
        var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
        return (ra.top - rb.top) || (ra.left - rb.left);
      });
      queue.forEach(function (el, i) {
        var d = Math.min(i, 7) * 65;
        el.style.setProperty('--hw-d', d + 'ms');
        el.classList.add('is-in');
        window.setTimeout(function () { finish(el); }, d + 800);
      });
      queue = [];
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        queue.push(entry.target);
      });
      if (queue.length && !flushing) { flushing = true; window.requestAnimationFrame(flush); }
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.06 });

    function tag(scope) {
      REVEAL.forEach(function (pair) {
        var list;
        try { list = scope.querySelectorAll(pair[0]); } catch (e) { return; }
        Array.prototype.forEach.call(list, function (el) {
          if (el.hasAttribute('data-hw-reveal') || el.hasAttribute('data-hw-seen')) return;
          el.setAttribute('data-hw-seen', '');
          // never hide what the visitor can already see, or what a filter has hidden
          var r = el.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) return;
          if (r.top < vh * 0.92) return;
          // cards parked off to the side in a swipe row are revealed by swiping,
          // not scrolling — leave them visible
          if (r.left >= (window.innerWidth || 0) || r.right <= 0) return;
          el.setAttribute('data-hw-reveal', pair[1]);
          io.observe(el);
        });
      });
    }

    tag(doc);

    // cards injected later (filters, pagination, recommendations)
    if ('MutationObserver' in window) {
      var pending = false;
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) {
            if (!pending) {
              pending = true;
              window.setTimeout(function () { pending = false; vh = window.innerHeight || vh; tag(doc); }, 120);
            }
            break;
          }
        }
      }).observe(doc.body, { childList: true, subtree: true });
    }

    // Safety net: nothing may stay invisible (e.g. a filter re-shows a card
    // that was never scrolled to, or the tab was in the background).
    function rescue() {
      var hidden = doc.querySelectorAll('[data-hw-reveal]:not(.is-in)');
      Array.prototype.forEach.call(hidden, function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < (window.innerHeight || vh) && r.bottom > 0 && (r.width || r.height)) {
          io.unobserve(el);
          el.classList.add('is-in');
          window.setTimeout(function () { finish(el); }, 800);
        }
      });
    }
    window.setInterval(rescue, 1500);
    window.addEventListener('pageshow', rescue);
    doc.addEventListener('click', function () { window.setTimeout(rescue, 260); }, true);
    doc.addEventListener('input', function () { window.setTimeout(rescue, 260); }, true);
  }

  /* ── 3. Cursor spotlight on cards ────────────────────────────────────────── */
  var SPOT = '.hw-parts-card, .hw-3d-card, .hwa-card, .hw-drops-card, .hw-tune-card, .hw-story__card';

  function initSpotlight() {
    if (reduce || !canHover) return;

    var style = doc.createElement('style');
    style.textContent =
      '.hw-spot{position:absolute;inset:0;z-index:1;border-radius:inherit;pointer-events:none;opacity:0;' +
      'transition:opacity .3s ease;background:radial-gradient(240px circle at var(--hw-mx,50%) var(--hw-my,50%),rgba(224,53,32,.13),transparent 62%)}' +
      '.hw-spot.is-on{opacity:1}';
    doc.head.appendChild(style);

    var current = null;
    var raf = 0;
    var lastX = 0, lastY = 0;

    function paint() {
      raf = 0;
      if (!current) return;
      var r = current.card.getBoundingClientRect();
      current.spot.style.setProperty('--hw-mx', (lastX - r.left) + 'px');
      current.spot.style.setProperty('--hw-my', (lastY - r.top) + 'px');
    }

    doc.addEventListener('pointerover', function (e) {
      var card = e.target.closest ? e.target.closest(SPOT) : null;
      if (!card || (current && current.card === card)) return;
      if (window.getComputedStyle(card).position === 'static') return;
      var spot = card.querySelector(':scope > .hw-spot');
      if (!spot) {
        spot = doc.createElement('span');
        spot.className = 'hw-spot';
        spot.setAttribute('aria-hidden', 'true');
        card.appendChild(spot);
      }
      if (current) current.spot.classList.remove('is-on');
      current = { card: card, spot: spot };
      spot.classList.add('is-on');
    }, { passive: true });

    doc.addEventListener('pointermove', function (e) {
      if (!current) return;
      if (!current.card.contains(e.target)) {
        current.spot.classList.remove('is-on');
        current = null;
        return;
      }
      lastX = e.clientX; lastY = e.clientY;
      if (!raf) raf = window.requestAnimationFrame(paint);
    }, { passive: true });
  }

  /* ── 4. Count-up for stats ───────────────────────────────────────────────── */
  function initCounters() {
    if (reduce || inEditor || !('IntersectionObserver' in window)) return;
    var els = doc.querySelectorAll('.hw-stat__number, .hw-parts-stat__num, .hw-3d-stat__num, .hwa-stat__n, .hwa-stat b');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0.6 });

    function run(el) {
      var node = el.firstChild;
      if (!node || node.nodeType !== 3) return;
      var raw = node.nodeValue;
      var m = raw.match(/^(\s*)(\d[\d,]*)(.*)$/);
      if (!m) return;
      var target = parseInt(m[2].replace(/,/g, ''), 10);
      if (!target || target < 3) return;
      var commas = m[2].indexOf(',') !== -1;
      var start = null;
      var dur = Math.min(1400, 500 + target / 40);

      function fmt(n) { return commas ? n.toLocaleString('en-US') : String(n); }
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        node.nodeValue = m[1] + fmt(Math.round(target * eased)) + m[3];
        if (p < 1) window.requestAnimationFrame(step);
        else node.nodeValue = raw;
      }
      window.requestAnimationFrame(step);
    }

    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  /* ── 5. "Added" confirmation on Add to Cart ──────────────────────────────── */
  function initAddedFeedback() {
    doc.addEventListener('ajaxProduct:added', function (e) {
      var btn = e.detail && e.detail.addToCartBtn;
      if (!btn || btn.classList.contains('hw-added')) return;
      var label = btn.querySelector('[data-add-to-cart-text]');
      if (!label) return;
      var before = label.textContent;
      btn.classList.add('hw-added');
      label.textContent = 'Added to cart ✓';
      window.setTimeout(function () {
        btn.classList.remove('hw-added');
        if (label.textContent === 'Added to cart ✓') label.textContent = before;
      }, 1800);
    });
  }

  ready(function () {
    try { initScroll(); } catch (e) {}
    try { initReveal(); } catch (e) {}
    try { initSpotlight(); } catch (e) {}
    try { initCounters(); } catch (e) {}
    try { initAddedFeedback(); } catch (e) {}
  });
})();
